import { NavLink, useNavigate } from 'react-router-dom';
import { can, type WorkspaceFeature } from '@itsappy/shared';
import { useChannels, useProjects, useSpaces } from '@/hooks/queries';
import { useAuth } from '@/store/auth';
import { useUi } from '@/store/ui';
import { cn } from '@/lib/utils';
import { Avatar, CountBadge, Dropdown, MenuItem } from '@/components/ui';
import { BrandLogo } from '@/components/BrandLogo';
import { effectiveProductSurface, getProductSurface, hasFullAccess } from '@/lib/product';
import {
  AutomationsIcon,
  BellIcon,
  ChannelIcon,
  ChatIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DarkThemeIcon,
  FilesIcon,
  BookIcon,
  ReportIcon,
  FlameIcon,
  GamesIcon,
  HomeIcon,
  LanguageIcon,
  LightThemeIcon,
  MailIcon,
  CalendarIcon,
  MeetingsIcon,
  PeopleIcon,
  GroupIcon,
  CompanyIcon,
  PersonIcon,
  ProjectsIcon,
  SettingsIcon,
  SignOutIcon,
  SpacesIcon,
  TasksIcon,
  WhiteboardIcon,
  type IconProps,
} from '@/components/ui/icons';

const NAV: {
  to: string;
  label: string;
  ar: string;
  Icon: React.ComponentType<IconProps>;
  end?: boolean;
}[] = [
  { to: '/', label: 'Home', ar: 'الرئيسية', Icon: HomeIcon, end: true },
  { to: '/spaces', label: 'Spaces', ar: 'المساحات', Icon: SpacesIcon },
  { to: '/projects', label: 'Projects', ar: 'المشاريع', Icon: ProjectsIcon },
  { to: '/repositories', label: 'Repositories', ar: 'مستودعات الكود', Icon: FilesIcon },
  { to: '/my-tasks', label: 'My tasks', ar: 'مهامي', Icon: TasksIcon },
  { to: '/automations', label: 'Automations', ar: 'الأتمتة', Icon: AutomationsIcon },
  { to: '/chat', label: 'Chat', ar: 'الدردشة', Icon: ChatIcon },
  { to: '/inbox', label: 'Inbox', ar: 'الوارد', Icon: BellIcon },
  { to: '/calendar', label: 'Calendar', ar: 'التقويم', Icon: CalendarIcon },
  { to: '/meetings', label: 'Meetings', ar: 'الاجتماعات', Icon: MeetingsIcon },
  { to: '/whiteboards', label: 'Whiteboards', ar: 'السبورات', Icon: WhiteboardIcon },
  { to: '/files', label: 'Files', ar: 'الملفات', Icon: FilesIcon },
  { to: '/mail', label: 'Email', ar: 'البريد', Icon: MailIcon },
  { to: '/games', label: 'Games', ar: 'الألعاب', Icon: GamesIcon },
  { to: '/engagement', label: 'Engagement', ar: 'التفاعل', Icon: FlameIcon },
  { to: '/learning', label: 'Learning', ar: 'التعلّم', Icon: BookIcon },
  { to: '/insights', label: 'Insights', ar: 'الرؤى', Icon: ReportIcon },
  { to: '/people', label: 'People', ar: 'الأشخاص', Icon: PeopleIcon },
  { to: '/company', label: 'Company', ar: 'الشركة', Icon: CompanyIcon },
  { to: '/team', label: 'Team', ar: 'الفريق', Icon: GroupIcon },
  // `end` so the nested workspace-requests route does not light this up too.
  { to: '/admin', label: 'Administration', ar: 'الإدارة', Icon: SettingsIcon, end: true },
  // Covers the request queue and the workspaces themselves; the page's own
  // tabs move between them, so one entry is enough.
  {
    to: '/admin/workspaces',
    label: 'Workspaces',
    ar: 'مساحات العمل',
    Icon: CompanyIcon,
  },
];

/** Navigation entries that exist only while their product does. */
const PRODUCT_ROUTES: Record<string, WorkspaceFeature | undefined> = {
  '/': 'home',
  '/spaces': 'spaces',
  '/projects': 'tasks',
  '/repositories': 'tasks',
  '/my-tasks': 'tasks',
  '/automations': 'tasks',
  '/chat': 'chat',
  '/calendar': 'meetings',
  '/meetings': 'meetings',
  '/games': 'games',
  '/engagement': 'engagement',
  '/learning': 'learning',
  '/insights': 'insights',
  '/company': 'company',
  '/mail': 'mail',
};

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="px-3 pb-1 pt-5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
      {children}
    </p>
  );
}

/** Comfortable tap targets on touch; the tighter desktop rhythm from `lg` up. */
const rowClass = (isActive: boolean, collapsed: boolean) =>
  cn(
    'flex min-h-[44px] items-center gap-2.5 rounded-lg text-sm transition-colors lg:min-h-0 lg:py-1.5',
    collapsed ? 'justify-center px-0 py-2' : 'px-3 py-2',
    isActive
      ? 'bg-surface-3 font-medium text-ink'
      : 'text-slate-400 hover:bg-surface-2 hover:text-slate-100',
  );

export function Sidebar({
  onNavigate,
  collapsed = false,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const { user, organizations, orgId, role, features, disabledFeatures, isPlatformAdmin, switchOrg, logout } =
    useAuth();
  const navigate = useNavigate();
  const toggleCollapsed = useUi((state) => state.toggleSidebarCollapsed);
  const theme = useUi((state) => state.theme);
  const language = useUi((state) => state.language);
  const toggleTheme = useUi((state) => state.toggleTheme);
  const toggleLanguage = useUi((state) => state.toggleLanguage);
  const fullAccess = hasFullAccess(features, disabledFeatures);
  const { data: spaces } = useSpaces(features.includes('spaces'));
  const { data: projects } = useProjects(features.includes('tasks'));
  const { data: channels } = useChannels(features.includes('chat'));
  const product = effectiveProductSurface(getProductSurface(), features);
  const visibleNav = product === 'chat'
    ? NAV.filter((item) => item.to === '/chat')
    : product === 'meet'
      ? NAV.filter((item) => item.to === '/calendar' || item.to === '/meetings')
      : NAV.filter((item) => {
          if (item.to === '/admin') return role === 'owner';
          // Team is the supervisory half of Company: reports, the audit trail
          // and the schedules behind check-in. Everything on it needs the
          // reports permission, so the link goes where the page would be empty.
          if (item.to === '/team') return role ? can(role, 'report:view') : false;
          // Reviewing workspace requests is a platform privilege, not a
          // workspace one — an owner without it must not see the queue.
          if (item.to === '/admin/workspaces') return isPlatformAdmin;
          // A product link follows the product: gone if this person was not
          // given it, and gone if the workspace switched it off — a full
          // account is not a reason to advertise a door that is locked.
          const feature = PRODUCT_ROUTES[item.to];
          if (feature) return features.includes(feature);
          // Everything else is the full-workspace surface.
          return fullAccess;
        });

  const arabic = language === 'ar';
  const dmChannels = channels?.items.filter((c) => c.kind === 'dm') ?? [];
  const unreadMessages = (channels?.items ?? []).reduce(
    (total, channel) => total + channel.unreadCount,
    0,
  );

  async function signOut() {
    onNavigate?.();
    await logout();
    navigate('/login');
  }

  const CollapseIcon = collapsed
    ? arabic
      ? ChevronLeftIcon
      : ChevronRightIcon
    : arabic
      ? ChevronRightIcon
      : ChevronLeftIcon;

  return (
    <nav
      aria-label="Main"
      className={cn(
        'flex max-w-[85vw] shrink-0 flex-col border-surface-2 bg-surface-1 transition-[width] duration-200 lg:max-w-none',
        arabic ? 'border-l' : 'border-r',
        collapsed ? 'w-[4.5rem]' : 'w-[17rem] lg:w-60',
      )}
    >
      <Dropdown
        align="left"
        className="w-56"
        label={arabic ? 'تبديل مساحة العمل' : 'Switch workspace'}
        trigger={
          <span
            className={cn(
              'flex w-full items-center gap-2 border-b border-surface-2 py-3 text-left transition-colors hover:bg-surface-2',
              collapsed ? 'justify-center px-0' : 'px-3',
            )}
          >
            {/*
              Collapsed there is only room for the mark; expanded, the full
              wordmark carries the brand on its own, so the workspace name is
              not repeated beside it. The name still identifies the current
              workspace inside the menu, where switching actually happens.
            */}
            {collapsed ? (
              <BrandLogo compact className="h-8 w-8" />
            ) : (
              <>
                <span className="min-w-0 flex-1">
                  {/* 2.55:1 is the wordmark's own ratio, so `object-contain`
                      has no letterboxing to centre and the logo sits flush
                      with the workspace label beneath it. */}
                  <BrandLogo className="h-9 w-24" />
                  <span className="mt-0.5 block text-[11px] text-slate-500">
                    {arabic ? 'مساحة العمل' : 'Workspace'}
                  </span>
                </span>
                <span className="shrink-0 text-slate-500">
                  <ChevronDownIcon />
                </span>
              </>
            )}
          </span>
        }
      >
        {(close) => (
          <>
            {organizations.map((o) => (
              <MenuItem
                key={o.id}
                selected={o.id === orgId}
                onClick={() => {
                  close();
                  if (o.id !== orgId) switchOrg(o.id);
                }}
              >
                <span className="flex-1 truncate">{o.name}</span>
                {o.id === orgId && (
                  <span className="text-brand-400">
                    <CheckIcon size="sm" />
                  </span>
                )}
              </MenuItem>
            ))}
            <div className="my-1 border-t border-surface-3" />
            {product === 'lab' && (fullAccess || role === 'owner') && <MenuItem
              onClick={() => {
                close();
                onNavigate?.();
                navigate('/settings');
              }}
            >
              {arabic ? 'إعدادات مساحة العمل' : 'Workspace settings'}
            </MenuItem>}
          </>
        )}
      </Dropdown>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        <div className="space-y-0.5 pt-2">
          {visibleNav.map(({ to, label, ar, Icon, end }) => {
            const name = arabic ? ar : label;
            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                title={collapsed ? name : undefined}
                aria-label={collapsed ? name : undefined}
                onClick={onNavigate}
                className={({ isActive }) => rowClass(isActive, collapsed)}
              >
                <span className="shrink-0 text-slate-400">
                  <Icon />
                </span>
                {!collapsed && <span className="min-w-0 flex-1 truncate">{name}</span>}
                {to === '/chat' && !collapsed && (
                  <CountBadge count={unreadMessages} label="unread messages" />
                )}
              </NavLink>
            );
          })}
        </div>

        {product === 'lab' && !collapsed && (spaces?.items.length ?? 0) > 0 && (
          <>
            <SectionLabel>{arabic ? 'المساحات' : 'Spaces'}</SectionLabel>
            <div className="space-y-0.5">
              {spaces!.items.map((space) => (
                <NavLink
                  key={space.id}
                  to={`/spaces/${space.id}`}
                  onClick={onNavigate}
                  className={({ isActive }) => rowClass(isActive, false)}
                >
                  {/* Lit only when somebody is actually in the space. */}
                  <span
                    className={cn(
                      'h-1.5 w-1.5 shrink-0 rounded-full',
                      space.occupancy > 0 ? 'bg-emerald-400' : 'bg-surface-4',
                    )}
                  />
                  <span className="min-w-0 flex-1 truncate">{space.name}</span>
                  {space.occupancy > 0 && (
                    <span className="shrink-0 rounded bg-surface-3 px-1.5 text-[10px] text-slate-300">
                      {space.occupancy}
                      <span className="sr-only"> inside</span>
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </>
        )}

        {product === 'lab' && !collapsed && (projects?.items.length ?? 0) > 0 && (
          <>
            <SectionLabel>{arabic ? 'المشاريع' : 'Projects'}</SectionLabel>
            <div className="space-y-0.5">
              {projects!.items.map((project) => (
                <NavLink
                  key={project.id}
                  to={`/projects/${project.id}`}
                  onClick={onNavigate}
                  className={({ isActive }) => rowClass(isActive, false)}
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-sm"
                    style={{ backgroundColor: project.color }}
                  />
                  <span className="min-w-0 flex-1 truncate">{project.name}</span>
                </NavLink>
              ))}
            </div>
          </>
        )}

        {product !== 'meet' && !collapsed && dmChannels.length > 0 && (
          <>
            <SectionLabel>{arabic ? 'الرسائل المباشرة' : 'Direct messages'}</SectionLabel>
            <div className="space-y-0.5">
              {dmChannels.slice(0, 8).map((channel) => (
                <NavLink
                  key={channel.id}
                  to={`/chat/${channel.id}`}
                  onClick={onNavigate}
                  className={({ isActive }) => rowClass(isActive, false)}
                >
                  {channel.peers[0] ? (
                    <Avatar user={channel.peers[0]} size="xs" showStatus />
                  ) : (
                    <span className="shrink-0 text-slate-500">
                      <ChannelIcon size="sm" />
                    </span>
                  )}
                  <span className="min-w-0 flex-1 truncate">{channel.name}</span>
                  <CountBadge count={channel.unreadCount} label="unread messages" />
                </NavLink>
              ))}
            </div>
          </>
        )}
      </div>

      {user && (
        <div className="border-t border-surface-2 p-2">
          <Dropdown
            align="left"
            className="bottom-full mb-1.5 mt-0 w-56"
            label={arabic ? 'الملف الشخصي والإعدادات' : 'Profile and settings'}
            trigger={
              <span
                className={cn(
                  'flex w-full items-center gap-2 rounded-lg py-2 text-left transition-colors hover:bg-surface-2',
                  collapsed ? 'justify-center px-0' : 'px-2',
                )}
              >
                <Avatar user={user} size="sm" showStatus />
                {!collapsed && (
                  <>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-ink">
                        {user.name}
                      </span>
                      <span className="block truncate text-[11px] text-slate-500">
                        {user.title ?? user.email}
                      </span>
                    </span>
                    <span className="shrink-0 text-slate-500">
                      <ChevronDownIcon />
                    </span>
                  </>
                )}
              </span>
            }
          >
            {(close) => (
              <>
                <div className="border-b border-surface-3 px-3 py-2.5">
                  <p className="truncate text-sm font-medium text-ink">{user.name}</p>
                  <p className="truncate text-xs text-slate-400">{user.email}</p>
                </div>
                <MenuItem
                  onClick={() => {
                    close();
                    onNavigate?.();
                    navigate('/settings');
                  }}
                >
                  <PersonIcon size="sm" />
                  {arabic ? 'الملف الشخصي والإعدادات' : 'Profile & settings'}
                </MenuItem>
                <MenuItem onClick={toggleTheme}>
                  {theme === 'dark' ? <LightThemeIcon size="sm" /> : <DarkThemeIcon size="sm" />}
                  {arabic
                    ? theme === 'dark'
                      ? 'استخدام المظهر الفاتح'
                      : 'استخدام المظهر الداكن'
                    : theme === 'dark'
                      ? 'Switch to light theme'
                      : 'Switch to dark theme'}
                </MenuItem>
                {/* The top bar hides its language control below `sm`, so the
                    account menu is where it stays reachable on a phone. */}
                <MenuItem onClick={toggleLanguage}>
                  <LanguageIcon size="sm" />
                  {arabic ? 'English' : 'العربية'}
                </MenuItem>
              </>
            )}
          </Dropdown>

          {/* Sign out and the collapse toggle share the footer rail. Neither is
              a destination, so neither belongs in the navigation list above. */}
          <div className={cn('mt-1 flex items-center gap-1', collapsed && 'flex-col')}>
            <button
              type="button"
              onClick={() => void signOut()}
              title={collapsed ? (arabic ? 'تسجيل الخروج' : 'Sign out') : undefined}
              aria-label={arabic ? 'تسجيل الخروج' : 'Sign out'}
              className={cn(
                'flex min-h-[44px] items-center gap-2.5 rounded-lg text-sm text-slate-400 transition-colors hover:bg-surface-2 hover:text-red-300 lg:min-h-0 lg:py-2',
                collapsed ? 'w-9 justify-center px-0 py-2' : 'flex-1 px-2 py-2',
              )}
            >
              <SignOutIcon />
              {!collapsed && <span>{arabic ? 'تسجيل الخروج' : 'Sign out'}</span>}
            </button>

            <button
              type="button"
              onClick={toggleCollapsed}
              aria-label={
                collapsed
                  ? arabic
                    ? 'توسيع الشريط الجانبي'
                    : 'Expand sidebar'
                  : arabic
                    ? 'تصغير الشريط الجانبي'
                    : 'Collapse sidebar'
              }
              title={collapsed ? (arabic ? 'توسيع' : 'Expand') : arabic ? 'تصغير' : 'Collapse'}
              className="hidden h-9 w-9 shrink-0 place-items-center rounded-lg text-slate-500 transition-colors hover:bg-surface-2 hover:text-slate-200 lg:grid"
            >
              <CollapseIcon />
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
